import React from 'react';
import { Modal, Button, Table, Badge, ListGroup } from 'react-bootstrap';
import { Node } from '@xyflow/react';

interface StepData extends Record<string, unknown> {
  label: string;
  step: any;
  groups?: any[];
  customFields?: any[];
  stepConfigs?: any[];
  status?: string;
  isCurrent?: boolean;
  isCompleted?: boolean;
  isPending?: boolean;
  description?: string;
}

interface WorkflowStepDetailsProps {
  show: boolean;
  onHide: () => void;
  node: Node<StepData> | null;
}

const WorkflowStepDetails: React.FC<WorkflowStepDetailsProps> = ({ show, onHide, node }) => {
  if (!node || !node.data) {
    return null;
  }

  const { step, groups, customFields, stepConfigs, status, description, isCurrent, isCompleted, isPending } = node.data;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Detalhes da Etapa: {node.data.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="step-details-container">
          {/* Informações Básicas */}
          <div className="mb-4">
            <h5>Informações Básicas</h5>
            <Table striped bordered size="sm">
              <tbody>
                <tr>
                  <td><strong>Nome</strong></td>
                  <td>{node.data.label}</td>
                </tr>
                {description && (
                  <tr>
                    <td><strong>Descrição</strong></td>
                    <td>{description}</td>
                  </tr>
                )}
                {status && (
                  <tr>
                    <td><strong>Status</strong></td>
                    <td>
                      <Badge bg={isCurrent ? 'primary' : isCompleted ? 'success' : 'secondary'}>
                        {status}
                      </Badge>
                    </td>
                  </tr>
                )}
                <tr>
                  <td><strong>Estado</strong></td>
                  <td>
                    {isCurrent && <Badge bg="primary" className="me-2">Atual</Badge>}
                    {isCompleted && <Badge bg="success" className="me-2">Concluído</Badge>}
                    {isPending && <Badge bg="secondary">Pendente</Badge>}
                  </td>
                </tr>
                {step?.previous_steps && step.previous_steps.length > 0 && (
                  <tr>
                    <td><strong>Passos Anteriores</strong></td>
                    <td>
                      {step.previous_steps.map((prev: string, idx: number) => (
                        <Badge key={idx} bg="info" className="me-1">{prev}</Badge>
                      ))}
                      <Badge bg="info" className="ms-2">Fluxo Alternativo</Badge>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Grupos Responsáveis */}
          {groups && groups.length > 0 && (
            <div className="mb-4">
              <h5>Grupos Responsáveis</h5>
              <ListGroup>
                {groups.map((group: any) => (
                  <ListGroup.Item key={group.id}>
                    <strong>{group.name}</strong>
                    {group.description && (
                      <div className="text-muted small">{group.description}</div>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {/* Campos Customizados */}
          {customFields && customFields.length > 0 && (
            <div className="mb-4">
              <h5>Campos Customizados Configurados</h5>
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Campo</th>
                    <th>Tipo</th>
                    <th>Visível</th>
                    <th>Obrigatório</th>
                  </tr>
                </thead>
                <tbody>
                  {customFields.map((field: any) => {
                    const config = stepConfigs?.find((c: any) => c.fieldId === field.id && c.step_name === step?.name);
                    return (
                      <tr key={field.id}>
                        <td>
                          <strong>{field.label}</strong>
                          <div className="text-muted small">{field.name}</div>
                        </td>
                        <td>
                          <Badge bg="secondary">{field.field_type}</Badge>
                        </td>
                        <td>
                          {config ? (
                            <Badge bg={config.is_visible ? 'success' : 'secondary'}>
                              {config.is_visible ? 'Sim' : 'Não'}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          {config ? (
                            <Badge bg={config.is_required ? 'danger' : 'secondary'}>
                              {config.is_required ? 'Sim' : 'Não'}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {(!groups || groups.length === 0) && (!customFields || customFields.length === 0) && (
            <div className="text-muted text-center py-3">
              <p>Nenhuma informação adicional configurada para esta etapa.</p>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkflowStepDetails;


